import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { 
  Sparkles, 
  TreeDeciduous, 
  MessageSquareText, 
  Target,
  ArrowRight,
  Brain,
  Lightbulb,
  Search
} from 'lucide-react'
import { Button, Card } from '@/components/common'

const LandingPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 sm:py-32">
        {/* Background decoration */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-light-baby-pink/30 dark:bg-dark-burnt-peach/20 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-light-icy-blue/30 dark:bg-dark-muted-teal/20 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-1/3 w-80 h-80 bg-light-mauve/30 dark:bg-dark-apricot-cream/20 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-light-mauve/20 dark:bg-light-mauve/10 text-purple-700 dark:text-light-mauve text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Curiosity-Driven Problem Design
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">
              <span className="text-gradient dark:text-gradient-dark">AADYA</span>
            </h1>
            <p className="text-lg sm:text-xl font-medium text-slate-700 dark:text-slate-200 mb-6">
              <span className="font-bold">A</span>gentic{' '}
              <span className="font-bold">A</span>I for{' '}
              <span className="font-bold">D</span>esigning{' '}
              <span className="font-bold">Y</span>our problems through{' '}
              <span className="font-bold">A</span>bstraction
            </p>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-6">
              Design the{' '}
              <span className="text-gradient dark:text-gradient-dark">
                Problem
              </span>
              , Not Just Solutions
            </h2>

            <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-8 max-w-2xl mx-auto">
              AADYA helps designers move from messy real-world situations to
              well-structured problem statements through AI-guided Socratic
              questioning, grounded in the AI3C framework
              (Activity, Item, Contradiction, Constraints, Criteria).
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/login">
                <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                  Start Designing Problems
                </Button>
              </Link>
              <a href="#how-it-works">
                <Button variant="secondary" size="lg">
                  Learn How It Works
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50/50 dark:bg-slate-800/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              Four AI Agents, One Goal
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              AADYA orchestrates specialized AI agents to guide your inquiry
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: <Search className="w-6 h-6" />,
                title: 'Cleaner AI (CAI)',
                description: 'Preprocesses your input, removing inadvertent solutions and biases',
                color: 'from-orange-400 to-orange-500',
              },
              {
                icon: <MessageSquareText className="w-6 h-6" />,
                title: 'Question AI (QAI)',
                description: 'Generates Socratic questions to probe deeper into your situation',
                color: 'from-pink-400 to-pink-500',
              },
              {
                icon: <Brain className="w-6 h-6" />,
                title: 'Answer AI (AAI)',
                description: 'Provides research support when you need help answering questions',
                color: 'from-teal-400 to-teal-500',
              },
              {
                icon: <Target className="w-6 h-6" />,
                title: 'Observer AI (OAI)',
                description: 'Synthesizes your inquiry into structured problem statements',
                color: 'from-purple-400 to-purple-500',
              },
            ].map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full text-center p-6" hover>
                  <div
                    className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center text-white mx-auto mb-4`}
                  >
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {feature.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              How AADYA Works
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              A curiosity-driven journey from situation to structured problem
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                step: 1,
                title: 'Describe Your Situation',
                description: 'Share the facts and observations about your design context. No solutions yet — just what you see and know.',
                icon: <Lightbulb className="w-6 h-6" />,
              },
              {
                step: 2,
                title: 'Answer Curiosity Questions',
                description: 'QAI generates questions that branch into sequential and parallel inquiries. Answer them, skip when unsure, or ask AAI for help.',
                icon: <MessageSquareText className="w-6 h-6" />,
              },
              {
                step: 3,
                title: 'Explore Until First Principles',
                description: 'Continue the inquiry until you reach fundamental truths. The conversation tree grows as you explore different angles.',
                icon: <TreeDeciduous className="w-6 h-6" />,
              },
              {
                step: 4,
                title: 'Generate Your Problem Statement',
                description: 'OAI analyzes your entire inquiry and produces a structured AI3C problem statement with design directions.',
                icon: <Target className="w-6 h-6" />,
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex gap-6 mb-8 last:mb-0"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-light-icy-blue to-light-mauve dark:from-dark-muted-teal dark:to-dark-apricot-cream flex items-center justify-center text-white font-bold">
                    {item.step}
                  </div>
                </div>
                <div className="flex-1 pb-8 border-b border-slate-200 dark:border-slate-700 last:border-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-light-icy-blue dark:text-dark-muted-teal">
                      {item.icon}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-900 dark:text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="text-slate-600 dark:text-slate-400">
                    {item.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* AI3C Framework Section */}
      <section className="py-20 bg-slate-50/50 dark:bg-slate-800/30">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">
              The AI3C Framework
            </h2>
            <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
              Every problem statement is structured with five essential elements
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
            {[
              { letter: 'A', label: 'Activity', desc: 'What is being done' },
              { letter: 'I', label: 'Item', desc: 'What it\'s done to' },
              { letter: '3C', label: 'Contradiction', desc: 'Why it\'s hard' },
              { letter: '', label: 'Constraints', desc: 'Boundaries to respect' },
              { letter: '', label: 'Criteria', desc: 'How to measure success' },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="text-center p-4 h-full">
                  <div className="text-2xl font-bold text-light-icy-blue dark:text-dark-muted-teal mb-1">
                    {item.letter || '•'}
                  </div>
                  <h4 className="font-semibold text-slate-900 dark:text-white mb-1">
                    {item.label}
                  </h4>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {item.desc}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="max-w-3xl mx-auto text-center p-8 sm:p-12 bg-gradient-to-br from-light-icy-blue/10 via-light-mauve/10 to-light-baby-pink/10 dark:from-dark-muted-teal/10 dark:via-dark-apricot-cream/10 dark:to-dark-burnt-peach/10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-4">
              Ready to Design Better Problems?
            </h2>
            <p className="text-slate-600 dark:text-slate-400 mb-6">
              Start your curiosity-driven problem design journey today.
            </p>
            <Link to="/login">
              <Button size="lg" rightIcon={<ArrowRight className="w-5 h-5" />}>
                Get Started Free
              </Button>
            </Link>
          </Card>
        </div>
      </section>
    </div>
  )
}

export default LandingPage
